import { useState } from 'react'

import { R } from 'core/helpers'
import { NewPaginator } from 'core/types/generated/utilities/types'

import { PaginationProps } from './Pagination'

type PageQuery = { [param: string]: string }

type CursorPagerResult = {
  /**
   * Cursor query params for the current page (undefined on the first page).
   * Spread into the query hook's `query` args — makeQueryHook has no
   * absolute-URL fetch path, and routing cursors through query params keeps
   * per-page react-query cache keys.
   */
  pageQuery: PageQuery | undefined
  /** Zero-based index of the current page; 0 on the first page. */
  pageIndex: number
  /** Return to the first page (call when filters or the resource change). */
  reset: () => void
  /**
   * Build Pagination props from the *current* response's pagination. Taking
   * the value at call time (not as a hook argument) lets the component call
   * this pager before the query hook it feeds.
   */
  getPaginationProps: (
    pagination: NewPaginator | undefined,
  ) => Pick<
    PaginationProps,
    'hasPrevious' | 'hasNext' | 'onPrevious' | 'onNext'
  >
}

// Cursor URLs are absolute in practice; the base makes relative ones safe too.
const parseQuery = (url: string): PageQuery =>
  Object.fromEntries(new URL(url, 'https://placeholder.invalid').searchParams)

const useCursorPager = (): CursorPagerResult => {
  // Visited pages, oldest first; the last entry is the current page.
  const [stack, setStack] = useState<Array<PageQuery | undefined>>([undefined])

  return {
    pageQuery: stack[stack.length - 1],
    pageIndex: stack.length - 1,
    reset: () => setStack([undefined]),
    getPaginationProps: (pagination) => {
      const nextUrl = pagination?.nextUrl

      return {
        hasPrevious: stack.length > 1,
        hasNext: nextUrl != null,
        onPrevious: () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
        onNext: () =>
          setStack((s) => {
            if (nextUrl == null) return s
            const next = parseQuery(nextUrl)
            return R.equals(next, s[s.length - 1]) ? s : [...s, next]
          }),
      }
    },
  }
}

export default useCursorPager
